import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { BlogPostShell, POSTS } from "@/pages/Blog";

const POST = POSTS.find((p) => p.slug === "judging-and-scoring-photography-competitions");
const OG = "https://cameraclubscore.com/og/blog-generic.png";
const URL = "https://cameraclubscore.com/blog/judging-and-scoring-photography-competitions";

export default function PostJudging() {
  return (
    <>
      <Helmet>
        <title>{`${POST.title} — Camera Club Score`}</title>
        <meta name="description" content={POST.excerpt} />
        <link rel="canonical" href={URL} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={URL} />
        <meta property="og:title" content={POST.title} />
        <meta property="og:description" content={POST.excerpt} />
        <meta property="og:image" content={OG} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={OG} />
      </Helmet>
      <BlogPostShell post={POST}>
        <p className="lead">
          Judging is where a club season is won or lost — not by the
          photographs, but by the members' trust in the process. Get this
          right and complaints disappear. Get it wrong and even brilliant
          images feel unfair. Here's what a decade of watching judging
          nights has taught us.
        </p>

        <h2>Should judges score out of 10 or 20?</h2>
        <p>
          Twenty gives more resolution — a judge who thinks image A is
          "just a bit better" than image B has a way to say it. But it also
          adds cognitive load: judges spend longer deciding whether
          something is a 16 or a 17.
        </p>
        <p>
          Our recommendation: <strong>use 10 with half-points allowed</strong>
          {" "}(so scores are 8, 8.5, 9, 9.5, 10). You get 20-point
          resolution without the paralysis of a 20-point scale.
        </p>

        <h2>How do judges record scores efficiently?</h2>
        <p>
          Three formats work, in order of speed:
        </p>
        <ol>
          <li><strong>Judge calls, secretary types.</strong> Fastest — but only if the secretary has a decent tool. Camera Club Score's Live Entry grid is designed for this: press Enter, jump to the next score, keep going.</li>
          <li><strong>Judge on a tablet.</strong> Best for hybrid clubs. Slight learning curve for older judges.</li>
          <li><strong>Judge on paper.</strong> Slow but reliable. Someone else has to transcribe afterwards.</li>
        </ol>

        <h2>Can scores be entered during judging?</h2>
        <p>
          Yes — this is how it should be done. Live entry means the
          judge sees the running total on-screen at the end, results are
          published instantly, and there's no risk of losing a paper sheet
          on the way to the treasurer's car.
        </p>

        <h2>How do we avoid duplicate scores?</h2>
        <p>
          Two habits: (1) every photographer has one row per image, so
          duplicates are visible instantly. (2) the running order is
          <strong> locked</strong> before judging starts, so nobody can
          accidentally add the same image twice mid-session.
        </p>

        <h2>How do we handle tied scores?</h2>
        <p>
          Three schools of thought:
        </p>
        <ul>
          <li><strong>Both share the rank.</strong> "Joint 3rd" — most common, feels fair.</li>
          <li><strong>Judge breaks the tie.</strong> Adds pressure to the judge but produces a clean league table.</li>
          <li><strong>Count next season's competitions as tiebreakers.</strong> Only really works at the end-of-season league.</li>
        </ul>
        <p>
          Camera Club Score defaults to "both share the rank" and shows a
          <strong> Tied</strong> chip next to the photographer's name so
          it's visible at a glance.
        </p>

        <h2>Should judges see previous scores?</h2>
        <p>
          <strong>Absolutely not during scoring.</strong> This is the
          single most important rule. A judge who's just given a 9 will
          unconsciously calibrate the next image against it — score
          drift is a real thing. Either hide previous scores from the
          judge's view, or run scoring blind and reveal at the end.
        </p>

        <h2>How do we produce judging reports?</h2>
        <p>
          A judging report is a PDF listing every entry with its score,
          placement, and the judge's name — printed and archived. In
          Camera Club Score this is a one-click export from the Results
          page.
        </p>

        <h2>How do we display results during judging?</h2>
        <p>
          Two options. The subtle one: a laptop on the scoring desk that
          shows the live grid. The showstopper: mirror the app on the
          projector for members to watch the leaderboard update in
          real-time. Judging becomes a bit theatrical — in a good way.
        </p>

        <p>
          Try any of this with <Link to="/">Camera Club Score</Link>. Free
          for camera clubs, no credit card, set up in two minutes.
        </p>
      </BlogPostShell>
    </>
  );
}
